import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Sparkles } from "lucide-react";

const EMPTY = {
  title:"", subtitle:"", destination:"", days:2, nights:1, with_stay:true, stay_details:"",
  vehicle_name:"", gallery_urls: [],
  price_per_person:0, child_price:0, inclusions:"", exclusions:"", itinerary:"", highlights:"",
  starts_on:"", ends_on:"", seats_available:20, poster_url:"", badge:"", active:true, featured:false,
};

const toArr = (s) => typeof s==="string" ? s.split("\n").map(x=>x.trim()).filter(Boolean) : (s||[]);
const toStr = (a) => Array.isArray(a) ? a.join("\n") : (a||"");

export default function AdminTours() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/tours/all").then(r=>setItems(r.data));
  useEffect(()=>{ load().catch(()=>{}); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        days: parseInt(form.days)||1, nights: parseInt(form.nights)||0,
        price_per_person: parseFloat(form.price_per_person)||0,
        child_price: parseFloat(form.child_price)||0,
        seats_available: parseInt(form.seats_available)||null,
        inclusions: toArr(form.inclusions), exclusions: toArr(form.exclusions),
        itinerary: toArr(form.itinerary), highlights: toArr(form.highlights),
      };
      if (editing) await api.put(`/tours/${editing}`, {...payload, id: editing});
      else await api.post("/tours", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if(!window.confirm("Delete tour?")) return; await api.delete(`/tours/${id}`); load(); toast.success("Deleted"); };
  const edit = (t) => { setEditing(t.id); setForm({...t, inclusions: toStr(t.inclusions), exclusions: toStr(t.exclusions), itinerary: toStr(t.itinerary), highlights: toStr(t.highlights)}); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-display font-black tracking-tight flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#ea580c]" /> Tour Packages</h1>
        <Button onClick={()=>{setEditing(null);setForm(EMPTY);setOpen(true);}} className="bg-[#ea580c] hover:bg-orange-700" data-testid="add-tour-btn"><Plus className="w-4 h-4 mr-1" /> Add Tour</Button>
      </div>
      <p className="text-sm text-slate-500 mt-2">Poster tours (Ashtavinayak, Balaji, Vaishnodevi…) shown on the public site.</p>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(t => (
          <div key={t.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={`tour-item-admin-${t.id}`}>
            {t.poster_url && <img src={t.poster_url} className="w-full aspect-video object-cover" alt={t.title} />}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold">{t.title}</div>
                  <div className="text-xs text-slate-500">{t.destination} · {t.days}D/{t.nights}N</div>
                </div>
                <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{t.active?"Active":"Off"}</div>
              </div>
              <div className="mt-2 text-sm font-semibold">₹{t.price_per_person.toLocaleString()}<span className="text-xs font-normal text-slate-500">/person</span></div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={()=>edit(t)} data-testid={`edit-tour-${t.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={()=>del(t.id)} data-testid={`delete-tour-${t.id}`}><Trash2 className="w-3 h-3 mr-1 text-red-500" /> Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?"Edit":"Add"} Tour</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Title</Label><Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="tour-title" /></div>
            <div className="md:col-span-2"><Label>Subtitle</Label><Input value={form.subtitle} onChange={e=>setForm({...form,subtitle:e.target.value})} /></div>
            <div><Label>Destination</Label><Input value={form.destination} onChange={e=>setForm({...form,destination:e.target.value})} /></div>
            <div><Label>Poster</Label>
              <div className="flex flex-col gap-2">
                <input type="file" accept="image/*" onChange={e=>{
                  const file = e.target.files?.[0]; if (!file) return;
                  if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB). Compress and try again."); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm(f=>({...f, poster_url: reader.result}));
                  reader.readAsDataURL(file);
                }} data-testid="tour-poster-file" className="text-xs" />
                <Input value={form.poster_url?.startsWith("data:") ? "(uploaded from device)" : form.poster_url} onChange={e=>setForm({...form,poster_url:e.target.value})} placeholder="Or paste image URL" data-testid="tour-poster-url" />
                {form.poster_url && <img src={form.poster_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg border" />}
              </div>
            </div>
            <div><Label>Days</Label><Input type="number" value={form.days} onChange={e=>setForm({...form,days:e.target.value})} /></div>
            <div><Label>Nights</Label><Input type="number" value={form.nights} onChange={e=>setForm({...form,nights:e.target.value})} /></div>
            <div className="flex items-end gap-2"><Label>Stay incl.</Label><Switch checked={form.with_stay} onCheckedChange={v=>setForm({...form,with_stay:v})} /></div>
            <div><Label>Badge</Label><Input value={form.badge} onChange={e=>setForm({...form,badge:e.target.value})} placeholder="Darshan Special" /></div>
            <div className="md:col-span-2"><Label>Stay Details</Label><Input value={form.stay_details} onChange={e=>setForm({...form,stay_details:e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Vehicle Name / Details (manual)</Label><Input value={form.vehicle_name} onChange={e=>setForm({...form,vehicle_name:e.target.value})} placeholder="e.g. 17-Seater AC Tempo Traveller MH-12-XX-1234" data-testid="tour-vehicle-name" /></div>
            <div className="md:col-span-2">
              <Label>Gallery Images (multiple)</Label>
              <input type="file" accept="image/*" multiple onChange={e=>{
                const files = Array.from(e.target.files||[]);
                files.forEach(f=>{
                  if (f.size > 3 * 1024 * 1024) { toast.error(`${f.name}: too large (max 3 MB)`); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm(s=>({...s, gallery_urls: [...(s.gallery_urls||[]), reader.result]}));
                  reader.readAsDataURL(f);
                });
              }} data-testid="tour-gallery-file" className="text-xs" />
              {form.gallery_urls?.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {form.gallery_urls.map((u, i) => (
                    <div key={i} className="relative">
                      <img src={u} alt={`gallery ${i}`} className="w-full h-20 object-cover rounded border" />
                      <button type="button" onClick={()=>setForm(s=>({...s, gallery_urls: s.gallery_urls.filter((_,x)=>x!==i)}))} className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full leading-none">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><Label>Price / Person ₹</Label><Input type="number" value={form.price_per_person} onChange={e=>setForm({...form,price_per_person:e.target.value})} /></div>
            <div><Label>Child Price ₹</Label><Input type="number" value={form.child_price} onChange={e=>setForm({...form,child_price:e.target.value})} /></div>
            <div><Label>Starts On</Label><Input type="date" value={form.starts_on||""} onChange={e=>setForm({...form,starts_on:e.target.value})} /></div>
            <div><Label>Ends On</Label><Input type="date" value={form.ends_on||""} onChange={e=>setForm({...form,ends_on:e.target.value})} /></div>
            <div><Label>Seats Available</Label><Input type="number" value={form.seats_available||0} onChange={e=>setForm({...form,seats_available:e.target.value})} /></div>
            <div className="flex items-end gap-4"><div className="flex items-center gap-2"><Label>Active</Label><Switch checked={form.active} onCheckedChange={v=>setForm({...form,active:v})} /></div><div className="flex items-center gap-2"><Label>Featured</Label><Switch checked={form.featured} onCheckedChange={v=>setForm({...form,featured:v})} /></div></div>
            <div className="md:col-span-2"><Label>Highlights (one per line)</Label><Textarea rows={3} value={form.highlights} onChange={e=>setForm({...form,highlights:e.target.value})} /></div>
            <div><Label>Inclusions (one per line)</Label><Textarea rows={4} value={form.inclusions} onChange={e=>setForm({...form,inclusions:e.target.value})} /></div>
            <div><Label>Exclusions (one per line)</Label><Textarea rows={4} value={form.exclusions} onChange={e=>setForm({...form,exclusions:e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Itinerary (one per line)</Label><Textarea rows={4} value={form.itinerary} onChange={e=>setForm({...form,itinerary:e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#ea580c] hover:bg-orange-700" data-testid="tour-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
